import { useCallback, useEffect, useSyncExternalStore } from "react"
import type { DailyCall, DailyInputSettings, DailyInputVideoProcessorSettings } from "@daily-co/daily-js"
import { useDaily, useDailyEvent, useInputSettings } from "@daily-co/daily-react"

type Processor = DailyInputVideoProcessorSettings
type Snapshot = {
  desired: Processor | null
  pending: "change" | "retry" | null
  waitingForVisibility: boolean
  error: string | null
}
type Store = {
  snapshot: Snapshot
  listeners: Set<() => void>
  operation: AbortController | null
  result?: Promise<boolean>
  activeSdk?: Promise<unknown>
  retryCameraStarted: boolean
  failures: number
  lastErrorEvent?: unknown
}

export const VIDEO_EFFECT_ERROR = "Background effects could not start. Your camera is off. Return to this tab and retry your camera with the background, or choose None in Settings."
const EMPTY: Snapshot = { desired: null, pending: null, waitingForVisibility: false, error: null }
const stores = new WeakMap<DailyCall, Store>()
const storeFor = (call: DailyCall) => {
  let store = stores.get(call)
  if (!store) {
    store = { snapshot: EMPTY, listeners: new Set(), operation: null, retryCameraStarted: false, failures: 0 }
    stores.set(call, store)
  }
  return store
}
const publish = (store: Store, patch: Partial<Snapshot>) => {
  store.snapshot = { ...store.snapshot, ...patch }
  store.listeners.forEach(listener => listener())
}
const stopCamera = (call: DailyCall) => {
  try { if (!call.isDestroyed()) call.setLocalVideo(false) } catch { /* Leaving or already destroyed. */ }
}
const cancelled = () => new DOMException("Video effect operation cancelled", "AbortError")
const check = (call: DailyCall, signal: AbortSignal) => {
  if (signal.aborted || call.isDestroyed()) throw cancelled()
}

const waitUntilVisible = (signal: AbortSignal) => new Promise<void>((resolve, reject) => {
  const clean = () => {
    document.removeEventListener("visibilitychange", visible)
    signal.removeEventListener("abort", abort)
  }
  const abort = () => { clean(); reject(cancelled()) }
  const visible = () => {
    if (document.visibilityState === "hidden") return
    clean()
    resolve()
  }
  if (signal.aborted) { abort(); return }
  document.addEventListener("visibilitychange", visible)
  signal.addEventListener("abort", abort, { once: true })
  visible()
})

const sameProcessor = (actual: Processor | undefined, desired: Processor) => {
  if (!actual && desired.type === "none") return true
  if (actual?.type !== desired.type) return false
  if (desired.type === "background-image" && actual.type === "background-image") {
    const a = actual.config?.source ?? actual.config?.url
    const b = desired.config?.source ?? desired.config?.url
    if (a instanceof ArrayBuffer && b instanceof ArrayBuffer) {
      const bytes = new Uint8Array(b)
      return a.byteLength === b.byteLength && new Uint8Array(a).every((byte, index) => byte === bytes[index])
    }
    return a === b
  }
  return desired.type !== "background-blur" || (actual.type === "background-blur" && (actual.config?.strength ?? 0.5) === (desired.config?.strength ?? 0.5))
}
const sameRequest = (left: Processor | null, right: Processor) => sameProcessor(left || undefined, right)

/** Invalidate user continuations, without pretending that Daily's active SDK request is cancellable. */
export const cancelVideoEffectOperations = (call: DailyCall) => {
  const store = stores.get(call)
  if (!store) return
  const interruptedBackground = Boolean(store.operation && store.snapshot.desired && store.snapshot.desired.type !== "none")
  store.operation?.abort()
  store.operation = null
  if (interruptedBackground) {
    stopCamera(call)
    publish(store, { error: VIDEO_EFFECT_ERROR })
  }
  if (store.retryCameraStarted) {
    stopCamera(call)
    // Daily queues processor updates behind camera start/stop. Before a room move, wait
    // for that queue to drain while muted; applying a preference is not proof of frames.
    const desired = store.snapshot.desired
    if (desired && !call.isDestroyed()) {
      const sdk = Promise.resolve().then(() => call.updateInputSettings({ video: { processor: desired } }))
      store.activeSdk = sdk
      void sdk.catch(() => {}).finally(() => { if (store.activeSdk === sdk) store.activeSdk = undefined })
    }
  }
  store.retryCameraStarted = false
  publish(store, { pending: null, waitingForVisibility: false })
}

export const isVideoEffectUpdateActive = (call: DailyCall) => Boolean(stores.get(call)?.activeSdk)
export const hasVideoEffectFailure = (call: DailyCall) => Boolean(stores.get(call)?.snapshot.error)

/** Room moves await SDK work that already started, never a request queued behind a hidden tab. */
export const waitForVideoEffectUpdate = async (call: DailyCall) => {
  try { await stores.get(call)?.activeSdk } catch { /* Persistent effect feedback owns the failure. */ }
}

export const recordVideoEffectError = (call: DailyCall, event: any) => {
  if (event?.type !== "video-processor-error" && !(event?.type === "input-settings-error" && event.details?.category === "processor" && event.details?.media?.includes("video"))) return
  const store = storeFor(call)
  if (store.lastErrorEvent === event) return
  store.lastErrorEvent = event
  store.failures += 1
  // Daily normally mutes after this error. Keep failure closed if its event precedes that update.
  stopCamera(call)
  publish(store, { error: VIDEO_EFFECT_ERROR })
}

const waitForPlayable = async (call: DailyCall, store: Store, desired: Processor, signal: AbortSignal, failures: number) => {
  const deadline = Date.now() + 15_000
  while (Date.now() < deadline) {
    check(call, signal)
    if (store.failures !== failures) throw new Error(VIDEO_EFFECT_ERROR)
    const settings = await call.getInputSettings()
    check(call, signal)
    const local = call.participants()?.local
    const canSend = local?.permissions?.canSend
    if (!local || canSend === false || (canSend instanceof Set && !canSend.has("video"))
      || local.tracks?.video?.state === "blocked") throw new Error(VIDEO_EFFECT_ERROR)
    const track = local?.tracks?.video?.track
    if (local?.video && local.tracks?.video?.state === "playable" && track?.readyState === "live" && track.enabled && !track.muted && sameProcessor(settings.video?.processor, desired)) return
    await new Promise<void>((resolve, reject) => {
      const abort = () => { window.clearTimeout(timer); reject(cancelled()) }
      const timer = window.setTimeout(() => { signal.removeEventListener("abort", abort); resolve() }, 100)
      signal.addEventListener("abort", abort, { once: true })
    })
  }
  throw new Error(VIDEO_EFFECT_ERROR)
}

const run = (call: DailyCall, desired: Processor, retry: boolean) => {
  const store = storeFor(call)
  if (store.snapshot.pending === (retry ? "retry" : "change") && sameRequest(store.snapshot.desired, desired) && store.result) return store.result
  cancelVideoEffectOperations(call)
  const operation = new AbortController()
  const { signal } = operation
  store.operation = operation
  publish(store, { desired, pending: retry ? "retry" : "change", waitingForVisibility: document.visibilityState === "hidden", error: retry ? store.snapshot.error : null })
  const result = (async () => {
    try {
      await waitForVideoEffectUpdate(call)
      check(call, signal)
      // A deferred preference must never become a deferred camera-on action.
      if (retry && document.visibilityState === "hidden") throw new Error(VIDEO_EFFECT_ERROR)
      if (desired.type !== "none" || retry) await waitUntilVisible(signal)
      check(call, signal)
      publish(store, { waitingForVisibility: false })
      const failures = store.failures
      const sdk = Promise.resolve().then(() => {
        check(call, signal)
        return call.updateInputSettings({ video: { processor: desired } })
      })
      store.activeSdk = sdk
      try { await sdk } finally { if (store.activeSdk === sdk) store.activeSdk = undefined }
      check(call, signal)
      // Daily can resolve the request while separately emitting a processor failure.
      const actual = await call.getInputSettings()
      check(call, signal)
      if (store.failures !== failures || !sameProcessor(actual.video?.processor, desired)) throw new Error(VIDEO_EFFECT_ERROR)
      if (retry) {
        check(call, signal)
        if (document.visibilityState === "hidden") throw new Error(VIDEO_EFFECT_ERROR)
        const local = call.participants()?.local
        const canSend = local?.permissions?.canSend
        const source = actual.video?.settings
        if (!local || canSend === false || (canSend instanceof Set && !canSend.has("video"))
          || local.tracks?.video?.state === "blocked"
          || (source && "customTrack" in source && source.customTrack.readyState === "ended")) throw new Error(VIDEO_EFFECT_ERROR)
        store.retryCameraStarted = true
        call.setLocalVideo(true)
        await waitForPlayable(call, store, desired, signal, failures)
      }
      check(call, signal)
      if (store.failures !== failures) throw new Error(VIDEO_EFFECT_ERROR)
      publish(store, { error: null })
      return true
    } catch (error) {
      if (!signal.aborted && !call.isDestroyed()) {
        if (retry || desired.type !== "none") stopCamera(call)
        publish(store, { error: VIDEO_EFFECT_ERROR })
      }
      return false
    } finally {
      if (store.operation === operation) {
        store.retryCameraStarted = false
        store.operation = null
        publish(store, { pending: null, waitingForVisibility: false })
      }
    }
  })()
  store.result = result
  return result
}

export const changeVideoEffect = (call: DailyCall, processor: Processor) => run(call, processor, false)
export const retryCameraWithVideoEffect = (call: DailyCall) => {
  const desired = storeFor(call).snapshot.desired
  return desired && desired.type !== "none" ? run(call, desired, true) : Promise.resolve(false)
}

/** Menus subscribe; their unmounts must not erase an operation or its desired background. */
export const useVideoEffects = () => {
  const call = useDaily()
  const { inputSettings } = useInputSettings()
  const subscribe = useCallback((listener: () => void) => {
    if (!call) return () => {}
    const store = storeFor(call)
    store.listeners.add(listener)
    return () => { store.listeners.delete(listener) }
  }, [call])
  const snapshot = useSyncExternalStore(subscribe, () => call ? storeFor(call).snapshot : EMPTY, () => EMPTY)
  useEffect(() => {
    if (!call || !inputSettings?.video?.processor || storeFor(call).snapshot.desired) return
    if (inputSettings.video.processor.type !== "none") publish(storeFor(call), { desired: inputSettings.video.processor })
  }, [call, inputSettings])
  useDailyEvent("nonfatal-error", useCallback((event) => { if (call) recordVideoEffectError(call, event) }, [call]))
  return { ...snapshot, call, inputSettings: inputSettings as DailyInputSettings | null }
}
